// src/components/UnityPlayer.tsx
"use client";

import { useEffect, useRef, useState } from "react";
import { theme } from "../styles/theme";

declare global {
  interface Window {
    createUnityInstance?: (
      canvas: HTMLCanvasElement,
      config: any,
      onProgress: (progress: number) => void
    ) => Promise<any>;
    unityInstance?: any;
  }
}

type UnityPlayerProps = {
  buildPath: string;
  buildName: string;
  projectName?: string;
  loadingLogoSrc?: string;
  /**
   * Unity "Compression Format" used for the build.
   * "br"/"gzip" builds emit Shooter.data.br, Shooter.wasm.br, etc. — the loader
   * itself is never compressed. Requires the server to send a matching
   * Content-Encoding header (Decompression Fallback must be OFF in Unity).
   */
  compression?: "none" | "br" | "gz";
};

export function UnityPlayer({
  buildPath,
  buildName,
  projectName = "Project",
  loadingLogoSrc = "/images/loading-logo.png",
  compression = "none",
}: UnityPlayerProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [unityLoaded, setUnityLoaded] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const unityInstanceRef = useRef<any>(null);

  // Suppress the portfolio-wide CRT overlay while a WebGL build is on screen.
  // The class is scoped to this component's lifetime, so any Unity page gets it
  // for free and the overlay comes back as soon as the player unmounts.
  useEffect(() => {
    document.body.classList.add("webgl-active");
    return () => {
      document.body.classList.remove("webgl-active");
    };
  }, []);

  useEffect(() => {
    const base = buildPath.endsWith("/") ? buildPath.slice(0, -1) : buildPath;

    // Unity leaves the loader uncompressed; everything else picks up the suffix.
    const ext = compression === "none" ? "" : `.${compression}`;

    const loaderUrl = `${base}/Build/${buildName}.loader.js`;
    const dataUrl = `${base}/Build/${buildName}.data${ext}`;
    const frameworkUrl = `${base}/Build/${buildName}.framework.js${ext}`;
    const codeUrl = `${base}/Build/${buildName}.wasm${ext}`;
    const streamingAssetsUrl = `${base}/StreamingAssets`;

    const script = document.createElement("script");
    script.src = loaderUrl;
    script.async = true;

    script.onload = async () => {
      if (!window.createUnityInstance) {
        setError("Unity loader script loaded, but createUnityInstance is missing.");
        return;
      }
      if (!canvasRef.current) {
        setError("Canvas element not found.");
        return;
      }

      try {
        const config = {
          dataUrl,
          frameworkUrl,
          codeUrl,
          streamingAssetsUrl,
          companyName: "DefaultCompany",
          productName: projectName,
          productVersion: "1.0",
        };

        const instance = await window.createUnityInstance(
          canvasRef.current,
          config,
          (progress: number) => {
            setLoadingProgress(progress * 100);
          }
        );

        unityInstanceRef.current = instance;
        window.unityInstance = instance;
        setUnityLoaded(true);
        console.log("[React] Unity instance loaded successfully");
      } catch (err) {
        console.error("[React] Error creating Unity instance:", err);
        setError("Failed to create Unity instance. Check build files and paths.");
      }
    };

    script.onerror = () => {
      console.error("[React] Failed to load Unity loader script:", loaderUrl);
      setError("Failed to load Unity loader script. Check buildPath/buildName.");
    };

    document.body.appendChild(script);

    return () => {
      if (unityInstanceRef.current) {
        unityInstanceRef.current.Quit?.().catch(() => {});
        unityInstanceRef.current = null;
      }
      if (window.unityInstance) {
        window.unityInstance = undefined;
      }
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, [buildPath, buildName, projectName, compression]);

  if (error) {
    return (
      <div
        style={{
          width: "100vw",
          height: "100dvh",
          background: theme.colors.Bg,
          color: theme.colors.Text,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          padding: "2rem",
          fontFamily: theme.fonts.body,
        }}
      >
        <div>
          <h2 style={{ fontSize: "1.5rem", marginBottom: "1rem" }}>
            Unity Build Not Found
          </h2>
          <p style={{ opacity: 0.8 }}>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        position: "relative",
        width: "100vw",
        height: "100dvh",
        background: theme.colors.Black,
        overflow: "hidden",
      }}
    >
      {!unityLoaded && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: theme.colors.DarkGray,
            color: theme.colors.White,
            zIndex: 10,
            fontFamily: theme.fonts.body,
          }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <img
              src={loadingLogoSrc}
              alt={`Loading ${projectName}`}
              style={{
                marginBottom: "1.5rem",
                width: "8rem",
                height: "auto",
                objectFit: "contain",
              }}
            />

            <div
              style={{
                width: "16rem",
                height: "0.9rem",
                background: "#1f2933",
                border: `1px solid ${theme.colors.Gray}`,
                boxSizing: "border-box",
              }}
            >
              <div
                style={{
                  height: "100%",
                  width: `${loadingProgress}%`,
                  backgroundColor: theme.colors.Green,
                  transition: "width 0.25s ease",
                }}
              />
            </div>

            <p
              style={{
                marginTop: "0.75rem",
                fontSize: "0.85rem",
                letterSpacing: "0.08em",
                textTransform: "uppercase",
              }}
            >
              Loading {projectName}... {Math.round(loadingProgress)}%
            </p>
          </div>
        </div>
      )}

      <canvas
        ref={canvasRef}
        id="unity-canvas"
        style={{
          width: "100%",
          height: "100%",
          display: "block",
          outline: "none",
        }}
      />
    </div>
  );
}
